/**
 * Tree View - Collapsible, searchable tree for Call Graph navigation
 * Issue #470: Implement collapsible tree view for Call Graph navigation
 *
 * Provides a file-explorer style interface for navigating large call graphs.
 * Functions are grouped by file/module with expandable callers/callees.
 */

import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { CallGraphData, CallGraphNode, CallGraphEdge } from '@veodiagram/shared';

interface TreeViewProps {
  data: CallGraphData;
  selectedFunctionId?: string;
  onSelectFunction?: (functionId: string) => void;
  onViewInExplorer?: (functionId: string) => void;
  onViewSubgraph?: (functionId: string) => void;
  onOpenSource?: (file: string, line: number) => void;
}

interface TreeNodeData {
  id: string;
  type: 'directory' | 'file' | 'function' | 'callers' | 'callees' | 'caller' | 'callee';
  label: string;
  depth: number;
  parentId?: string;
  functionNode?: CallGraphNode;
  childCount?: number;
  filePath?: string;
}

export function TreeView({
  data,
  selectedFunctionId,
  onSelectFunction,
  onViewInExplorer,
  onViewSubgraph,
  onOpenSource,
}: TreeViewProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const treeRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Build caller/callee maps
  const { callerMap, calleeMap } = useMemo(() => {
    const callerMap = new Map<string, CallGraphEdge[]>();
    const calleeMap = new Map<string, CallGraphEdge[]>();

    for (const edge of data.edges) {
      // Callees: functions that this function calls (source -> target)
      if (!calleeMap.has(edge.source)) {
        calleeMap.set(edge.source, []);
      }
      calleeMap.get(edge.source)!.push(edge);

      // Callers: functions that call this function (target <- source)
      if (!callerMap.has(edge.target)) {
        callerMap.set(edge.target, []);
      }
      callerMap.get(edge.target)!.push(edge);
    }

    return { callerMap, calleeMap };
  }, [data.edges]);

  // Node lookup map
  const nodeMap = useMemo(() => {
    const map = new Map<string, CallGraphNode>();
    for (const node of data.nodes) {
      map.set(node.id, node);
    }
    return map;
  }, [data.nodes]);

  // Group functions by file/directory
  const fileTree = useMemo(() => {
    const tree = new Map<string, Map<string, CallGraphNode[]>>();

    for (const node of data.nodes) {
      const filePath = node.location?.file || node.module || 'unknown';
      const parts = filePath.split('/');
      const fileName = parts.pop() || filePath;
      const dirPath = parts.join('/') || '.';

      if (!tree.has(dirPath)) {
        tree.set(dirPath, new Map());
      }
      const dirMap = tree.get(dirPath)!;
      if (!dirMap.has(fileName)) {
        dirMap.set(fileName, []);
      }
      dirMap.get(fileName)!.push(node);
    }

    // Sort directories and files
    const sortedTree = new Map(
      [...tree.entries()].sort((a, b) => a[0].localeCompare(b[0]))
    );

    for (const [dir, files] of sortedTree) {
      sortedTree.set(
        dir,
        new Map([...files.entries()].sort((a, b) => a[0].localeCompare(b[0])))
      );
    }

    return sortedTree;
  }, [data.nodes]);

  // Filter nodes by search query
  const matchesSearch = useCallback(
    (node: CallGraphNode): boolean => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        node.name.toLowerCase().includes(query) ||
        node.qualifiedName.toLowerCase().includes(query) ||
        node.module.toLowerCase().includes(query)
      );
    },
    [searchQuery]
  );

  // Build flat list of visible tree nodes
  const visibleNodes = useMemo(() => {
    const nodes: TreeNodeData[] = [];

    for (const [dirPath, files] of fileTree) {
      const dirId = `dir:${dirPath}`;
      const dirHasMatches = [...files.values()].flat().some(matchesSearch);

      if (!searchQuery || dirHasMatches) {
        // Add directory node
        nodes.push({
          id: dirId,
          type: 'directory',
          label: dirPath,
          depth: 0,
          childCount: [...files.values()].flat().length,
        });

        if (expandedNodes.has(dirId) || searchQuery) {
          for (const [fileName, functions] of files) {
            const fileId = `file:${dirPath}/${fileName}`;
            const fileHasMatches = functions.some(matchesSearch);

            if (!searchQuery || fileHasMatches) {
              // Add file node
              nodes.push({
                id: fileId,
                type: 'file',
                label: fileName,
                depth: 1,
                parentId: dirId,
                childCount: functions.length,
                filePath: `${dirPath}/${fileName}`,
              });

              if (expandedNodes.has(fileId) || searchQuery) {
                // Sort functions by name
                const sortedFunctions = [...functions].sort((a, b) =>
                  a.name.localeCompare(b.name)
                );

                for (const func of sortedFunctions) {
                  if (!searchQuery || matchesSearch(func)) {
                    const funcId = `func:${func.id}`;
                    const callers = callerMap.get(func.id) || [];
                    const callees = calleeMap.get(func.id) || [];

                    // Add function node
                    nodes.push({
                      id: funcId,
                      type: 'function',
                      label: func.name,
                      depth: 2,
                      parentId: fileId,
                      functionNode: func,
                    });

                    if (expandedNodes.has(funcId)) {
                      // Add callers group
                      if (callers.length > 0) {
                        const callersId = `callers:${func.id}`;
                        nodes.push({
                          id: callersId,
                          type: 'callers',
                          label: `Callers (${callers.length})`,
                          depth: 3,
                          parentId: funcId,
                          childCount: callers.length,
                        });

                        if (expandedNodes.has(callersId)) {
                          for (const edge of callers) {
                            const callerNode = nodeMap.get(edge.source);
                            if (callerNode) {
                              nodes.push({
                                id: `caller:${func.id}:${edge.source}`,
                                type: 'caller',
                                label: callerNode.name,
                                depth: 4,
                                parentId: callersId,
                                functionNode: callerNode,
                              });
                            }
                          }
                        }
                      }

                      // Add callees group
                      if (callees.length > 0) {
                        const calleesId = `callees:${func.id}`;
                        nodes.push({
                          id: calleesId,
                          type: 'callees',
                          label: `Calls (${callees.length})`,
                          depth: 3,
                          parentId: funcId,
                          childCount: callees.length,
                        });

                        if (expandedNodes.has(calleesId)) {
                          for (const edge of callees) {
                            const calleeNode = nodeMap.get(edge.target);
                            if (calleeNode) {
                              nodes.push({
                                id: `callee:${func.id}:${edge.target}`,
                                type: 'callee',
                                label: calleeNode.name,
                                depth: 4,
                                parentId: calleesId,
                                functionNode: calleeNode,
                              });
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }

    return nodes;
  }, [fileTree, expandedNodes, searchQuery, matchesSearch, callerMap, calleeMap, nodeMap]);

  // Toggle node expansion
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Handle node click
  const handleNodeClick = useCallback(
    (node: TreeNodeData, event: React.MouseEvent) => {
      event.stopPropagation();

      if (node.type === 'function' || node.type === 'caller' || node.type === 'callee') {
        if (node.functionNode) {
          onSelectFunction?.(node.functionNode.id);
        }
      }

      // Toggle expansion for expandable nodes
      if (
        node.type === 'directory' ||
        node.type === 'file' ||
        node.type === 'function' ||
        node.type === 'callers' ||
        node.type === 'callees'
      ) {
        toggleExpand(node.id);
      }
    },
    [onSelectFunction, toggleExpand]
  );

  // Handle double-click to view in explorer
  const handleNodeDoubleClick = useCallback(
    (node: TreeNodeData) => {
      if (node.functionNode) {
        onViewInExplorer?.(node.functionNode.id);
      }
    },
    [onViewInExplorer]
  );

  // Handle opening source file
  const handleOpenSource = useCallback(
    (node: TreeNodeData, event: React.MouseEvent) => {
      event.stopPropagation();
      if (node.functionNode?.location) {
        onOpenSource?.(node.functionNode.location.file, node.functionNode.location.startLine);
      }
    },
    [onOpenSource]
  );

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      const currentNode = visibleNodes[focusedIndex];

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => Math.min(prev + 1, visibleNodes.length - 1));
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => Math.max(prev - 1, 0));
          break;
        case 'ArrowRight':
          event.preventDefault();
          if (currentNode && !expandedNodes.has(currentNode.id)) {
            toggleExpand(currentNode.id);
          }
          break;
        case 'ArrowLeft':
          event.preventDefault();
          if (currentNode && expandedNodes.has(currentNode.id)) {
            toggleExpand(currentNode.id);
          }
          break;
        case 'Enter':
          event.preventDefault();
          if (currentNode) {
            if (currentNode.functionNode) {
              onSelectFunction?.(currentNode.functionNode.id);
            }
            toggleExpand(currentNode.id);
          }
          break;
        case '/':
          event.preventDefault();
          searchInputRef.current?.focus();
          break;
      }
    },
    [visibleNodes, focusedIndex, expandedNodes, toggleExpand, onSelectFunction]
  );

  // Scroll focused node into view
  useEffect(() => {
    const focusedElement = treeRef.current?.querySelector(`[data-index="${focusedIndex}"]`);
    focusedElement?.scrollIntoView({ block: 'nearest' });
  }, [focusedIndex]);

  // Get icon for node type
  const getNodeIcon = (node: TreeNodeData): string => {
    switch (node.type) {
      case 'directory':
        return expandedNodes.has(node.id) ? '📂' : '📁';
      case 'file':
        return '📄';
      case 'function':
        const func = node.functionNode;
        if (func?.isEntryPoint) return '🚀';
        if (func?.isAsync) return '⚡';
        if (func?.type === 'method') return '🔷';
        if (func?.type === 'arrow') return '➡️';
        return '🔹';
      case 'callers':
        return '📥';
      case 'callees':
        return '📤';
      case 'caller':
        return '↩️';
      case 'callee':
        return '↪️';
      default:
        return '•';
    }
  };

  // Highlight search matches
  const highlightMatch = (text: string): React.ReactNode => {
    if (!searchQuery) return text;

    const index = text.toLowerCase().indexOf(searchQuery.toLowerCase());
    if (index === -1) return text;

    return (
      <>
        {text.slice(0, index)}
        <mark className="bg-yellow-300 dark:bg-yellow-600 rounded px-0.5">
          {text.slice(index, index + searchQuery.length)}
        </mark>
        {text.slice(index + searchQuery.length)}
      </>
    );
  };

  // Expand all/collapse all
  const expandAll = useCallback(() => {
    const allIds = new Set<string>();
    for (const [dirPath, files] of fileTree) {
      allIds.add(`dir:${dirPath}`);
      for (const [fileName] of files) {
        allIds.add(`file:${dirPath}/${fileName}`);
      }
    }
    setExpandedNodes(allIds);
  }, [fileTree]);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set());
  }, []);

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-800">
      {/* Search and controls */}
      <div className="flex-shrink-0 p-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-2">
          <div className="relative flex-1">
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search functions... (press /)"
              className="w-full pl-8 pr-8 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            onClick={expandAll}
            className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-2 py-1 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
          >
            Collapse All
          </button>
          <span className="ml-auto text-gray-500 dark:text-gray-400">
            {visibleNodes.length} items • {data.nodes.length} functions
          </span>
        </div>
      </div>

      {/* Tree */}
      <div
        ref={treeRef}
        className="flex-1 overflow-auto p-2 focus:outline-none"
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        {visibleNodes.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No matches found' : 'No functions to display'}
          </div>
        ) : (
          <div className="space-y-0.5">
            {visibleNodes.map((node, index) => {
              const isSelected =
                node.functionNode?.id === selectedFunctionId;
              const isFocused = index === focusedIndex;
              const isExpanded = expandedNodes.has(node.id);
              const hasChildren =
                node.type === 'directory' ||
                node.type === 'file' ||
                node.type === 'function' ||
                node.type === 'callers' ||
                node.type === 'callees';

              return (
                <div
                  key={node.id}
                  data-index={index}
                  onClick={(e) => handleNodeClick(node, e)}
                  onDoubleClick={() => handleNodeDoubleClick(node)}
                  className={`
                    group flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer text-sm
                    ${isSelected ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-100' : ''}
                    ${isFocused && !isSelected ? 'bg-gray-100 dark:bg-gray-700' : ''}
                    ${!isSelected && !isFocused ? 'hover:bg-gray-50 dark:hover:bg-gray-700/50' : ''}
                  `}
                  style={{ paddingLeft: `${node.depth * 16 + 8}px` }}
                >
                  {/* Expand/collapse indicator */}
                  {hasChildren ? (
                    <span className="w-4 text-gray-400 text-xs">
                      {isExpanded ? '▼' : '▶'}
                    </span>
                  ) : (
                    <span className="w-4" />
                  )}

                  {/* Icon */}
                  <span className="text-sm">{getNodeIcon(node)}</span>

                  {/* Label */}
                  <span className="flex-1 truncate">
                    {highlightMatch(node.label)}
                  </span>

                  {/* Child count badge */}
                  {node.childCount !== undefined && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                      {node.childCount}
                    </span>
                  )}

                  {/* Action buttons for functions */}
                  {node.functionNode && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100">
                      {node.functionNode.location && (
                        <button
                          onClick={(e) => handleOpenSource(node, e)}
                          className="p-0.5 text-gray-400 hover:text-blue-500"
                          title="Open source"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </button>
                      )}
                      {onViewSubgraph && (
                        <button
                          onClick={(e) => { e.stopPropagation(); onViewSubgraph(node.functionNode!.id); }}
                          className="text-xs text-purple-500 hover:underline flex-shrink-0"
                          title="View Subgraph"
                        >
                          Subgraph
                        </button>
                      )}
                    </div>
                  )}

                  {/* Function type badge */}
                  {node.functionNode?.type && node.type === 'function' && (
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      {node.functionNode.type}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with keyboard hints */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-gray-200 dark:border-gray-700 text-xs text-gray-400 dark:text-gray-500">
        <span className="mr-4">↑↓ Navigate</span>
        <span className="mr-4">←→ Collapse/Expand</span>
        <span className="mr-4">Enter Select</span>
        <span>/ Search</span>
      </div>
    </div>
  );
}
